import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, quote } from "@/lib/subscription";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Users, Loader2 } from "lucide-react";

export function usePlans() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/seat-plans").then((r) => setData(r.data)); }, []);
  return data;
}

export function CycleToggle({ cycle, setCycle }) {
  return (
    <div className="inline-flex rounded-full border border-[#E5E0D8] bg-white p-1" data-testid="plan-cycle-toggle">
      {[["yearly", "Billed yearly"], ["monthly", "Billed monthly"]].map(([k, l]) => (
        <button key={k} onClick={() => setCycle(k)} data-testid={`plan-cycle-${k}`}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${cycle === k ? "bg-[#0D5C3A] text-white" : "text-stone-600 hover:text-stone-900"}`}>{l}</button>
      ))}
    </div>
  );
}

function PlanCard({ plan, cycle, busy, onPick, current, featured }) {
  const [seats, setSeats] = useState(plan.min);
  const q = quote(plan, cycle, seats);
  const range = plan.max == null ? `${plan.min}+ employees` : `${plan.min}–${plan.max} employees`;
  return (
    <div data-testid={`plan-card-${plan.id}`} className={`relative flex flex-col rounded-3xl border p-6 transition-transform hover:-translate-y-1 ${featured ? "border-[#0D5C3A] bg-[#0D5C3A] text-white shadow-xl" : "border-[#E5E0D8] bg-white"}`}>
      {featured && <span className="absolute -top-3 left-6 rounded-full bg-[#C85A32] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">Popular</span>}
      <p className="font-heading text-xl font-extrabold">{plan.name}</p>
      <p className={`mt-1 flex items-center gap-1.5 text-sm ${featured ? "text-white/75" : "text-stone-500"}`}><Users className="h-4 w-4" /> {range}</p>
      <p className="mt-6 font-heading text-4xl font-extrabold">{inr(plan.monthly)}<span className={`text-sm font-medium ${featured ? "text-white/70" : "text-stone-500"}`}> / employee / month</span></p>
      <p className={`mt-1 text-sm ${featured ? "text-white/75" : "text-stone-500"}`}>{inr(q.yearlyPerSeat)} per employee / year</p>
      {plan.max == null && (
        <label className={`mt-4 text-xs ${featured ? "text-white/80" : "text-stone-500"}`}>Employees (min {plan.min})
          <Input type="number" min={plan.min} value={seats} onChange={(e) => setSeats(parseInt(e.target.value || "0", 10))} className="mt-1 h-9 bg-white text-stone-900" data-testid={`plan-seats-${plan.id}`} />
        </label>
      )}
      <div className={`mt-5 border-t pt-4 ${featured ? "border-white/20" : "border-[#F0ECE1]"}`}>
        <p className={`text-xs uppercase tracking-wider ${featured ? "text-white/60" : "text-stone-400"}`}>Total {cycle === "yearly" ? "per year" : "per month"} · {q.seats} employees</p>
        <p className="mt-1 font-heading text-2xl font-bold" data-testid={`plan-total-${plan.id}`}>{inr(q.amount)}</p>
      </div>
      <Button disabled={busy || current || (plan.max == null && seats < plan.min)} onClick={() => onPick(plan, q.seats)} data-testid={`plan-start-${plan.id}`}
        className={`mt-6 h-11 rounded-full ${featured ? "bg-white text-[#0D5C3A] hover:bg-[#FAF8F5]" : "bg-[#0D5C3A] hover:bg-[#084229]"}`}>
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : current ? <Check className="mr-2 h-4 w-4" /> : null}
        {current ? "Current plan" : onPick.label || "Start Free Trial"}
      </Button>
    </div>
  );
}

export function PlanGrid({ plans, cycle, busyId, onPick, currentId }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
      {plans.map((p) => <PlanCard key={p.id} plan={p} cycle={cycle} busy={busyId === p.id} onPick={onPick} current={currentId === p.id} featured={p.id === "growth"} />)}
    </div>
  );
}
